import { PopupConfig } from "@/store/types";
import { cloneConfig, wbcLabConfig, White_Blood_Cell_Count } from "./config";

const AWICU_SPARKLINE_MINUTES = 60 * 24;

export function awicu() {
  const clonedConfig = cloneConfig();
  clonedConfig.global.showOrderSet = true;

  clonedConfig.census.gridCardClass = "GridCardIcu";
  clonedConfig.census.attending.sparkline.forEach((s: any) => {
    s.range = AWICU_SPARKLINE_MINUTES;
  });

  clonedConfig.body.patientDetailBodyComponent = "PatientDetailICU";
  clonedConfig.body.defaultPhoneLabel = "RN Phone";

  clonedConfig.parts.lungs.co2Title = "PaCO<sub>2</sub>";
  clonedConfig.parts.lungs.co2Atom = "paCo2";

  clonedConfig.parts.brain.atomOverride = "camRass";

  const defaultPopupConfig: PopupConfig = {
    graphRanges: [
      { text: "12 hours", hours: 12 },
      { text: "24 hours", hours: 24 },
      { text: "3 days", hours: 24 * 3 },
      { text: "7 days", hours: 24 * 7 }
    ],
    graphStartingIndex: 0
  };
  clonedConfig.defaultPopupDescription = defaultPopupConfig;

  const pagingFields = [
    { key: "who", text: "Who" },
    { key: "delay", text: "Delay" },
    { key: "repage", text: "Re-Page" },
    { key: "repeat", text: "Repeat" },
    { key: "reenable", text: "Snooze" }
  ];

  clonedConfig.help.paging.fields = clonedConfig.help.paging.fields.concat(
    pagingFields
  );

  clonedConfig.help.paging.description.repage = true;
  clonedConfig.help.paging.description.snooze = true;

  clonedConfig.body.buttons.LowMapAlert.visible = false;
  clonedConfig.labConfigs.splice(1, 0, {
    title: "Lactate",
    atom: "lactate",
    popupName: "Lactate"
  });
  clonedConfig.labConfigs.splice(4, 0, wbcLabConfig);

  clonedConfig.popups.White_Blood_Cell_Count = White_Blood_Cell_Count;

  clonedConfig.popups.Lactate = {
    component: "GenericRiskAndGraph",
    popupConfig: {
      title: "Lactate",

      graphStartingIndex: 0,
      graphs: [
        {
          title: "Lactate",
          source: "Lactate"
        },
        {
          title: "Bicarbonate",
          source: "Bicarb"
        }
      ]
    }
  };

  const threeHoursList = ["GenericRiskAndGraph"];
  // Update all graphs to use 12hrs or 3days if a generic (labs) popup
  Object.values(clonedConfig.popups).forEach((popup: any) => {
    if (popup.popupConfig && popup.component) {
      const index = threeHoursList.includes(popup.component) ? 2 : 0;
      popup.popupConfig.graphStartingIndex = index;
    }
  });

  clonedConfig.census.colorLegend = [
    { caseState: 1 },
    { caseState: 3 },
    { caseState: 4 },
    { caseState: 5 },
    { caseState: 6 }
  ];

  clonedConfig.census.LegendDescription = [
    {
      icon: "alertPhys1.png",
      label: "One or more active physiologic alert(s)"
    },
    {
      icon: "alertPhys2.png",
      label: "One or more severe active physiologic alert(s)"
    },
    {
      icon: "alertPhys3.gif",
      label: "Paging for an active alert"
    },
    { icon: "alertETT1.png", label: "Intubation risk factors" },
    { icon: "alertETT2.png", label: "History of difficult intubation" },
    { icon: "alertPump1.png", label: "LVAD" },
    { icon: "alertPump2.png", label: "Balloon Pump" },
    { icon: "alertPump3.png", label: "ECMO/RVAD/BiVAD/TAH" },
    { icon: "alertDialysis1.png", label: "Hemodialysis" },
    {
      icon: "alertDialysis2.png",
      label: "CRRT"
    },
    { icon: "alertVentilator.png", label: "Patient ventilated" },
    { icon: "alertVentilator1.png", label: "High tidal volume" },
    { icon: "alertVentilator2.png", label: "Paging for high tidal volume" },
    { icon: "alertNO.png", label: "Nitric Oxide" },
    { icon: "alertSOFA1.png", label: "Elevated SOFA" },
    { icon: "alertSOFA2.png", label: "High SOFA or qSOFA" },
    { icon: "alertICP1.png", label: "On ICP monitor with normal ICP and CPP" },
    {
      icon: "alertICP2.png",
      label: "Alerted ICP monitor for high ICP or low CPP"
    },
    {
      icon: "alertUlcer1.png",
      label: "Ulcer grade 1 or 2"
    },
    {
      icon: "alertUlcer2.png",
      label: "Ulcer grade 3 or 4"
    },
    { icon: "alertRestraint.png", label: "In restraints" },
    { icon: "alertJehovah.png", label: "Refuses blood transfusions" },
    { icon: "alertIsolation.png", label: "Contact or Isolation Precaution" },
    {
      icon: "alertIsolation2.png",
      label: "High Risk Contact or Isolation Precaution"
    },
    { icon: "alertSettings2.png", label: "Paging limits has been changed" },
    { icon: "alertSettings3.png", label: "Settings changed by order set" },
    { icon: "COVID-19_1.png", label: "COVID-19 test missing or inactive." },
    { icon: "COVID-19_2.png", label: "COVID-19 test result negative." },
    { icon: "COVID-19_3.png", label: "COVID-19 test result pending." },
    {
      icon: "COVID-19_4.png",
      label: "COVID-19 test inactive, previously positive."
    },
    { icon: "COVID-19_5.png", label: "COVID-19 test positive." }
  ];

  clonedConfig.census.legend = [
    {
      label: "Alert",
      paths: ["alertPhys1", "alertPhys2"]
    },
    {
      label: "Airway Status",
      paths: ["alertETT1", "alertETT2"]
    },
    {
      label: "MCS Pump",
      paths: ["alertPump1", "alertPump2", "alertPump3"]
    },
    {
      label: "Dialysis",
      paths: ["alertDialysis1", "alertDialysis2"]
    },
    {
      label: "On Ventilator",
      paths: ["alertVentilator", "alertVentilator1", "alertVentilator2"]
    },
    {
      label: "Nitric Oxide",
      paths: ["alertNO"]
    },
    {
      label: "SOFA",
      paths: ["alertSOFA1", "alertSOFA2"]
    },
    {
      label: "ICP Monitor",
      paths: ["alertICP1", "alertICP2"]
    },
    {
      label: "Ulcer",
      paths: ["alertUlcer1", "alertUlcer2"]
    },
    {
      label: "Precaution",
      paths: ["alertIsolation", "alertIsolation2"]
    },
    {
      label: "COVID-19 Status",
      paths: [
        "COVID-19_1",
        "COVID-19_2",
        "COVID-19_3",
        "COVID-19_4",
        "COVID-19_5"
      ]
    },
    {
      label: "In Restraints",
      paths: ["alertRestraint"]
    },
    {
      label: "Refuses Blood",
      paths: ["alertJehovah"]
    },
    {
      label: "Limits Changed",
      paths: ["alertSettings2", "alertSettings3"]
    }
  ];

  clonedConfig.body.bodyValues.brain.visible = false;

  clonedConfig.body.buttons.sofa.visible = true;
  clonedConfig.body.buttons.sirs.visible = true;
  clonedConfig.body.buttons.deteriorationIndex.visible = true;
  clonedConfig.body.buttons.weight.visible = true;
  clonedConfig.body.buttons.WeightAndIoBalance.visible = true;
  clonedConfig.body.buttons.ioBalance.visible = false;

  clonedConfig.popups.Brain.display.showIcuRisks = true;

  clonedConfig.census.legend.forEach((item: any) => {
    item.paths = item.paths.map((path: string) => {
      return `${path}.png`;
    });
  });
  return clonedConfig;
}
